import { useState, type ReactNode } from 'react';
import type { StakeholderRole } from '@wedpo/soc2';
import { roleShortLabels } from '@wedpo/soc2';
import { OwnerBadge, ControlChips } from '@/components/common';
import { useLocalStorage, type ViewId } from '@/lib/store';

/* small inline icons for the per-row evidence actions */
const ICON = {
  eye: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
      <path d="M1.5 12S5 5.5 12 5.5 22.5 12 22.5 12 19 18.5 12 18.5 1.5 12 1.5 12z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  pencil: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
    </svg>
  ),
  clip: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
      <path d="M21 11.5l-8.5 8.5a5 5 0 0 1-7-7l8.5-8.5a3.3 3.3 0 0 1 4.7 4.7l-8.5 8.5a1.7 1.7 0 0 1-2.4-2.4l7.9-7.9" />
    </svg>
  ),
} as const;

/* ---------- status model ----------
 * Every implementation item cycles through the same three states, persisted to
 * localStorage so progress sticks across visits. */
type Status = 'todo' | 'doing' | 'done';
const CYCLE: Status[] = ['todo', 'doing', 'done'];
const nextStatus = (s: Status): Status =>
  CYCLE[(CYCLE.indexOf(s) + 1) % CYCLE.length];
const ST_META: Record<Status, { cls: string; label: string }> = {
  todo: { cls: 's-gap', label: 'Not started' },
  doing: { cls: 's-partial', label: 'In progress' },
  done: { cls: 's-met', label: 'Done' },
};

/* ---------- evidence ----------
 * Uploaded evidence is persisted (with a data URL) to localStorage so a file
 * captured in one session is still shown on the next visit. */
interface EvidenceFile {
  name: string;
  type: string;
  size: number;
  savedOn: string;
  dataUrl?: string; // omitted for large files that would blow the storage quota
}

const MAX_INLINE_BYTES = 2_000_000; // store the preview only for files under ~2MB

const fmtBytes = (n: number) =>
  n < 1024
    ? `${n} B`
    : n < 1024 * 1024
      ? `${(n / 1024).toFixed(0)} KB`
      : `${(n / (1024 * 1024)).toFixed(1)} MB`;

const readFile = (file: File) =>
  new Promise<EvidenceFile>((resolve) => {
    const meta: EvidenceFile = {
      name: file.name,
      type: file.type,
      size: file.size,
      savedOn: new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }),
    };
    if (file.size > MAX_INLINE_BYTES) {
      resolve(meta);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => resolve({ ...meta, dataUrl: reader.result as string });
    reader.onerror = () => resolve(meta);
    reader.readAsDataURL(file);
  });

/* ---------- catalogs ----------
 * Real WeDPO templates, bundled under /templates. Policies, plans, procedures,
 * and evidence records are branded, editable HTML (open in a new tab, edit
 * inline, then Download PDF). */
interface ImplItem {
  id: string;
  name: string;
  controls: string[];
  role: StakeholderRole;
  defaultStatus: Status;
  cadence?: string;
  desc?: string;
  open?: string; // HTML template opened in a new tab
  openLabel?: string; // label for the open button (default "Open template")
  download?: string; // Word template downloaded
  explain?: string; // collapsible "what this is" copy (technical / artifacts)
  example?: string; // collapsible "evidence to capture" example
}

/* Every policy "Edit" opens the rich interactive Policy Library, deep-linked to
 * that policy via the URL hash. The hash key is the library's key for the
 * policy (a few differ from the standalone file name). */
const LIB = (key: string) => `/templates/policies/policy-library.html#${key}`;
const POLICIES: ImplItem[] = [
  { id: 'pol-isp', name: 'Information Security Policy', controls: ['DCF-13', 'DCF-32'], role: 'management', defaultStatus: 'done', open: LIB('information-security-policy'), openLabel: 'Edit' },
  { id: 'pol-access', name: 'Access Control Policy', controls: ['DCF-10'], role: 'it', defaultStatus: 'doing', open: LIB('access-control-policy'), openLabel: 'Edit' },
  { id: 'pol-risk', name: 'Risk Assessment Policy', controls: ['DCF-15'], role: 'risk', defaultStatus: 'todo', open: LIB('risk-assessment-policy'), openLabel: 'Edit' },
  { id: 'pol-vendor', name: 'Vendor Management Policy', controls: ['DCF-168'], role: 'risk', defaultStatus: 'todo', open: LIB('vendor-management-policy'), openLabel: 'Edit' },
  { id: 'pol-crypto', name: 'Cryptography (Encryption) Policy', controls: ['DCF-181'], role: 'engineering', defaultStatus: 'done', open: LIB('cryptography-encryption-policy'), openLabel: 'Edit' },
  { id: 'pol-asset', name: 'Asset Management Policy', controls: ['DCF-182'], role: 'it', defaultStatus: 'todo', open: LIB('asset-management-policy'), openLabel: 'Edit' },
  { id: 'pol-vuln', name: 'Vulnerability Management Policy', controls: ['DCF-183'], role: 'security', defaultStatus: 'todo', open: LIB('vulnerability-management-policy'), openLabel: 'Edit' },
  { id: 'pol-sdlc', name: 'Secure Development (SDLC) Policy', controls: ['DCF-31'], role: 'engineering', defaultStatus: 'todo', open: LIB('software-development-life-cycle-sdlc-policy'), openLabel: 'Edit' },
  { id: 'pol-aup', name: 'Acceptable Use Policy', controls: ['DCF-37'], role: 'it', defaultStatus: 'todo', open: LIB('acceptable-use-policy'), openLabel: 'Edit' },
  { id: 'pol-coc', name: 'Code of Conduct', controls: ['DCF-44'], role: 'hr', defaultStatus: 'done', open: LIB('code-of-conduct'), openLabel: 'Edit' },
  { id: 'pol-phys', name: 'Physical Security Policy', controls: ['DCF-94'], role: 'it', defaultStatus: 'todo', open: LIB('physical-security-policy'), openLabel: 'Edit' },
  { id: 'pol-dpp', name: 'Data Protection Policy', controls: ['DCF-45'], role: 'legal', defaultStatus: 'doing', open: LIB('data-protection-policy'), openLabel: 'Edit' },
  { id: 'pol-dataclass', name: 'Data Classification Policy', controls: ['DCF-102'], role: 'legal', defaultStatus: 'todo', open: LIB('data-classification-policy'), openLabel: 'Edit' },
  { id: 'pol-retention', name: 'Data Retention & Disposal Policy', controls: ['DCF-123', 'DCF-101'], role: 'legal', defaultStatus: 'todo', open: LIB('data-retention-and-disposal-policy'), openLabel: 'Edit' },
  { id: 'pol-ir', name: 'Incident Response Plan', controls: ['DCF-159'], role: 'security', defaultStatus: 'todo', open: LIB('incident-response-plan'), openLabel: 'Edit' },
  { id: 'pol-bcp', name: 'Business Continuity Plan', controls: ['DCF-166'], role: 'management', defaultStatus: 'todo', open: LIB('business-continuity-plan'), openLabel: 'Edit' },
  { id: 'pol-drp', name: 'Disaster Recovery Plan', controls: ['DCF-25'], role: 'engineering', defaultStatus: 'todo', open: LIB('disaster-recovery-plan'), openLabel: 'Edit' },
  { id: 'pol-backup', name: 'Backup Policy', controls: ['DCF-169'], role: 'engineering', defaultStatus: 'todo', open: LIB('backup-policy'), openLabel: 'Edit' },
  { id: 'pol-roles', name: 'Roles & Responsibilities', controls: ['DCF-42'], role: 'management', defaultStatus: 'doing', open: LIB('information-security-roles-and-responsibilities'), openLabel: 'Edit' },
  { id: 'pol-password', name: 'Password & Authentication Policy', controls: ['DCF-68'], role: 'it', defaultStatus: 'doing', open: LIB('password-and-authentication-policy'), openLabel: 'Edit' },
];

const PROCEDURES: ImplItem[] = [
  { id: 'proc-provision', name: 'Access Provisioning Procedure', controls: ['DCF-69'], role: 'it', defaultStatus: 'doing', desc: 'Request and grant least-privilege access.', open: '/templates/procedures/access-provisioning.html' },
  { id: 'proc-deprovision', name: 'Access Deprovisioning / Offboarding', controls: ['DCF-70'], role: 'it', defaultStatus: 'todo', desc: 'Revoke access on departure within SLA.', open: '/templates/procedures/access-deprovisioning.html' },
  { id: 'proc-change', name: 'Change Management Procedure', controls: ['DCF-5', 'DCF-4', 'DCF-7'], role: 'engineering', defaultStatus: 'doing', desc: 'Request, review, test, and approve changes.', open: '/templates/procedures/change-management.html' },
  { id: 'proc-patch', name: 'Patch & Update Management', controls: ['DCF-152', 'DCF-51'], role: 'engineering', defaultStatus: 'todo', desc: 'Triage and apply patches on cadence.', open: '/templates/procedures/patch-management.html' },
  { id: 'proc-breach', name: 'Breach / Incident Notification', controls: ['DCF-135'], role: 'security', defaultStatus: 'todo', desc: 'Notify affected parties and regulators.', open: '/templates/procedures/breach-notification.html' },
  { id: 'proc-disposal', name: 'Data Disposal & Destruction', controls: ['DCF-123', 'DCF-109'], role: 'it', defaultStatus: 'todo', desc: 'Securely destroy data at end of life.', open: '/templates/procedures/data-disposal.html' },
];

const EVIDENCE: ImplItem[] = [
  { id: 'ev-access-review', name: 'User Access Review', controls: ['DCF-11'], role: 'it', cadence: 'Quarterly', defaultStatus: 'todo', open: '/templates/evidence/user-access-review.html' },
  { id: 'ev-risk-report', name: 'Periodic Risk Assessment Report', controls: ['DCF-16'], role: 'risk', cadence: 'Annual', defaultStatus: 'todo', open: '/templates/evidence/risk-assessment-report.html' },
  { id: 'ev-fraud', name: 'Fraud Risk Assessment', controls: ['DCF-778'], role: 'risk', cadence: 'Annual', defaultStatus: 'todo', open: '/templates/evidence/fraud-risk-assessment.html' },
  { id: 'ev-board', name: 'Board / Management Meeting Minutes', controls: ['DCF-146'], role: 'management', cadence: 'Quarterly', defaultStatus: 'doing', open: '/templates/evidence/board-meeting-minutes.html' },
  { id: 'ev-ir-roster', name: 'Incident Response Team Roster', controls: ['DCF-29'], role: 'security', cadence: 'Annual', defaultStatus: 'todo', open: '/templates/evidence/ir-team-roster.html' },
  { id: 'ev-ir-test', name: 'Incident Response Test Report', controls: ['DCF-154'], role: 'security', cadence: 'Annual', defaultStatus: 'todo', open: '/templates/evidence/ir-test-report.html' },
  { id: 'ev-post-incident', name: 'Post-Incident Review', controls: ['DCF-30'], role: 'security', cadence: 'Per-event', defaultStatus: 'todo', open: '/templates/evidence/post-incident-review.html' },
  { id: 'ev-vuln-scan', name: 'Vulnerability Scan Tracking', controls: ['DCF-18'], role: 'security', cadence: 'Monthly', defaultStatus: 'doing', open: '/templates/evidence/vulnerability-scan-tracking.html' },
  { id: 'ev-training', name: 'Security Awareness Training Record', controls: ['DCF-36'], role: 'hr', cadence: 'Annual', defaultStatus: 'todo', open: '/templates/evidence/security-awareness-training.html' },
  { id: 'ev-vendor-review', name: 'Vendor Compliance Review', controls: ['DCF-57'], role: 'risk', cadence: 'Annual', defaultStatus: 'todo', open: '/templates/evidence/vendor-compliance-review.html' },
];

const TECHNICAL: ImplItem[] = [
  {
    id: 'tech-mfa', name: 'Multi-factor authentication', controls: ['DCF-67'], role: 'it', defaultStatus: 'done',
    explain: 'Require a second authentication factor (authenticator app, push, or hardware key) on every account that can reach in-scope systems, including admin consoles and the identity provider itself.',
    example: 'A screenshot of your IdP’s MFA enforcement policy showing MFA required for all users, plus an export of per-user enrollment status.',
  },
  {
    id: 'tech-sso', name: 'Authentication / SSO configuration', controls: ['DCF-68'], role: 'it', defaultStatus: 'done',
    explain: 'Centralize authentication through a single identity provider so access, password rules, and de-provisioning are enforced in one place.',
    example: 'The SSO configuration page for a core app showing it federates to your IdP, plus the IdP password and session policy.',
  },
  {
    id: 'tech-unique-id', name: 'Unique user IDs', controls: ['DCF-71'], role: 'it', defaultStatus: 'done',
    explain: 'Every user authenticates with their own named account; no shared or generic logins for in-scope systems.',
    example: 'A user-list export from a key system showing named accounts, with a note confirming shared/service accounts are vaulted and excluded.',
  },
  {
    id: 'tech-privileged', name: 'Privileged access restricted', controls: ['DCF-59'], role: 'it', defaultStatus: 'doing',
    explain: 'Administrative and production privileges are granted only to the roles that need them, reviewed regularly, and separated from day-to-day accounts.',
    example: 'An admin/role membership export for your cloud console plus the approval record for the most recent privileged grant.',
  },
  {
    id: 'tech-enc-transit', name: 'Encryption in transit', controls: ['DCF-55'], role: 'engineering', defaultStatus: 'done',
    explain: 'All traffic to and between in-scope services is encrypted with TLS 1.2 or higher; plaintext protocols are disabled.',
    example: 'An SSL Labs (or internal scan) report for your public endpoints showing TLS 1.2/1.3 and an A grade, plus the HTTPS-redirect/HSTS config.',
  },
  {
    id: 'tech-disk-enc', name: 'Hard-disk encryption on devices', controls: ['DCF-52'], role: 'it', defaultStatus: 'doing',
    explain: 'Full-disk encryption is enforced on every laptop and workstation that can access company data.',
    example: 'An MDM dashboard showing FileVault/BitLocker enabled across the fleet, with a per-device compliance export.',
  },
  {
    id: 'tech-antimalware', name: 'Antimalware on devices', controls: ['DCF-50'], role: 'it', defaultStatus: 'todo',
    explain: 'Endpoint protection / EDR is installed, running, and updating on all managed devices.',
    example: 'An EDR console view showing coverage and last-seen across devices, plus a sample detection or signature-update log.',
  },
  {
    id: 'tech-remote', name: 'Encrypted remote production access', controls: ['DCF-92'], role: 'engineering', defaultStatus: 'todo',
    explain: 'Access to production is brokered through an encrypted, authenticated channel (VPN, bastion, or zero-trust proxy) with MFA, never direct or plaintext.',
    example: 'The bastion/VPN config showing MFA and encryption, plus an access-log sample for a production session.',
  },
  {
    id: 'tech-monitoring', name: 'Continuous control monitoring', controls: ['DCF-160'], role: 'security', defaultStatus: 'todo',
    explain: 'Security-relevant events and control health are logged centrally, retained, and alerted on so deviations are caught during the window.',
    example: 'A screenshot of your logging/SIEM dashboard with retention settings, plus one configured alert rule and a sample alert.',
  },
];

const ARTIFACTS: ImplItem[] = [
  {
    id: 'art-org-chart', name: 'Organization chart', controls: ['DCF-14'], role: 'hr', defaultStatus: 'done', desc: 'Reporting lines and assignment of authority.',
    explain: 'A current organization chart showing reporting lines and where security, IT, and privacy responsibilities sit.',
    example: 'An exported org chart (PDF/PNG) dated within the audit window, showing the security function and its reporting line.',
  },
  {
    id: 'art-network', name: 'Network diagram', controls: ['DCF-22'], role: 'engineering', defaultStatus: 'doing', desc: 'Topology of the in-scope environment.',
    explain: 'A diagram of the in-scope environment: trust boundaries, key components, data stores, and how traffic flows between them.',
    example: 'A network/architecture diagram showing VPCs/subnets, the database tier, ingress points, and external dependencies.',
  },
  {
    id: 'art-asset-register', name: 'Asset inventory / register', controls: ['DCF-20'], role: 'it', defaultStatus: 'todo', desc: 'Devices, systems, and data stores in scope.',
    explain: 'A maintained register of the devices, systems, and data stores in scope, each with an owner and classification.',
    example: 'A spreadsheet or CMDB export listing each in-scope asset, its owner, classification, and environment.',
  },
  {
    id: 'art-risk-register', name: 'Risk register', controls: ['DCF-16'], role: 'risk', defaultStatus: 'doing', desc: 'Tracked risks with likelihood and impact.',
    explain: 'A living register of identified risks with likelihood, impact, owner, and treatment status, feeding the annual risk assessment.',
    example: 'A risk-register export showing each risk scored for likelihood/impact with a named owner and mitigation status.',
  },
  {
    id: 'art-sysdesc', name: 'System description (Section III)', controls: [], role: 'management', defaultStatus: 'doing', desc: 'Management description of the system.', open: '/system-description-report.html',
    explain: 'Management’s written description of the system (Section III of the SOC 2 report): services, boundaries, controls, and subservice organizations.',
    example: 'The drafted System Description document covering infrastructure, software, people, data, and the control environment.',
  },
];

const TEMPLATES_INDEX = '/templates/soc2-templates-center-index.docx';

type Category = 'policies' | 'procedures' | 'evidence' | 'technical' | 'artifacts' | 'index';

export function Implementation({ go }: { go: (v: ViewId) => void }) {
  const [active, setActive] = useState<Category | null>(null);
  const [status, setStatus] = useLocalStorage<Record<string, Status>>('implStatus', {});
  const [evidence, setEvidence] = useLocalStorage<Record<string, EvidenceFile[]>>(
    'implEvidence',
    {},
  );

  const statusOf = (item: ImplItem) => status[item.id] ?? item.defaultStatus;
  const bump = (id: string, current: Status) =>
    setStatus((prev) => ({ ...prev, [id]: nextStatus(current) }));
  const doneCount = (items: ImplItem[]) =>
    items.filter((i) => statusOf(i) === 'done').length;

  const addEvidence = (id: string, files: EvidenceFile[]) =>
    setEvidence((prev) => ({ ...prev, [id]: [...(prev[id] ?? []), ...files] }));
  const removeEvidence = (id: string, idx: number) =>
    setEvidence((prev) => ({
      ...prev,
      [id]: (prev[id] ?? []).filter((_, i) => i !== idx),
    }));

  const CARDS: {
    key: Category;
    ic: string;
    title: string;
    desc: string;
    items: ImplItem[];
  }[] = [
    { key: 'policies', ic: '📄', title: 'Policies & plans', desc: 'The 20 WeDPO policy and plan templates, each mapped to its controls.', items: POLICIES },
    { key: 'procedures', ic: '📋', title: 'Procedures & SOPs', desc: 'Step-by-step runbooks: provisioning, change, patching, breach, disposal.', items: PROCEDURES },
    { key: 'evidence', ic: '🗂', title: 'Evidence templates', desc: 'Record templates you fill on cadence to prove each control is operating.', items: EVIDENCE },
    { key: 'technical', ic: '⚙', title: 'Technical controls', desc: 'Configuration-based controls: MFA, SSO, encryption, monitoring.', items: TECHNICAL },
    { key: 'artifacts', ic: '📐', title: 'Artifacts', desc: 'Diagrams and registers: org chart, network, asset and risk registers.', items: ARTIFACTS },
  ];

  if (active) {
    const meta: Record<Category, { title: string; sub: string; items: ImplItem[] }> = {
      policies: {
        title: 'Policies & plans',
        sub: 'Download each WeDPO template, customize it for your company, then track it to done.',
        items: POLICIES,
      },
      procedures: {
        title: 'Procedures & SOPs',
        sub: 'Open each runbook, adapt the steps to your tools, and track it to done.',
        items: PROCEDURES,
      },
      evidence: {
        title: 'Evidence templates',
        sub: 'Each record template proves a control is operating. Fill it on its cadence.',
        items: EVIDENCE,
      },
      technical: {
        title: 'Technical controls',
        sub: 'Stand up each configuration control and capture a screenshot or export as evidence.',
        items: TECHNICAL,
      },
      artifacts: {
        title: 'Artifacts',
        sub: 'Produce each diagram or register. The system description opens the Section III template.',
        items: ARTIFACTS,
      },
      index: { title: '', sub: '', items: [] },
    };

    return (
      <section>
        <p className="eyebrow">Phase 03 · Implement</p>
        <button className="gp-back" onClick={() => setActive(null)}>
          ← All implementation areas
        </button>
        {active === 'index' ? (
          <IndexPanel statusOf={statusOf} />
        ) : (
          <>
            {active === 'policies' && (
              <div className="impl-feature">
                <div className="impl-feature-txt">
                  <strong>WeDPO Policy Library</strong>
                  <span>
                    The interactive library: pick any policy, fill the setup once to
                    populate every template, edit inline, and export to PDF. The
                    per-policy editors below stay in sync with your controls.
                  </span>
                </div>
                <button
                  className="btn btn-grad impl-feature-btn"
                  onClick={() =>
                    window.open(
                      '/templates/policies/policy-library.html',
                      '_blank',
                      'noopener',
                    )
                  }
                >
                  Open policy library →
                </button>
              </div>
            )}
            <ItemPanel
              title={meta[active].title}
              sub={meta[active].sub}
              items={meta[active].items}
              statusOf={statusOf}
              onCycle={bump}
              evidenceMode={active === 'technical' || active === 'artifacts'}
              evidence={evidence}
              onAddEvidence={addEvidence}
              onRemoveEvidence={removeEvidence}
              headerAction={
                active === 'policies' ? (
                  <button className="btn btn-ghost" onClick={() => go('policies')}>
                    Open policy editor →
                  </button>
                ) : undefined
              }
            />
          </>
        )}
      </section>
    );
  }

  return (
    <section>
      <p className="eyebrow">Phase 03 · Implement</p>
      <h1 className="title">Implement</h1>
      <p className="sub">
        Your Templates Center. Each area below manages one part of the build: adopt the
        policies, write the procedures, collect the evidence, stand up the technical
        controls, and produce the artifacts the audit will test.
      </p>

      <ManageBanner />

      <div className="impl-grid">
        {CARDS.map((c) => {
          const done = doneCount(c.items);
          const pct = Math.round((done / c.items.length) * 100);
          return (
            <div className="impl-card" key={c.key}>
              <div className="impl-card-top">
                <span className="impl-card-ic" aria-hidden>
                  {c.ic}
                </span>
                <span className="impl-card-count">
                  {done}/{c.items.length}
                </span>
              </div>
              <h3>{c.title}</h3>
              <p>{c.desc}</p>
              <div className="gp-bar gp-bar-sm" aria-hidden>
                <span className="gp-fill" style={{ width: `${pct}%` }} />
              </div>
              <button
                className="btn btn-grad impl-card-btn"
                onClick={() => setActive(c.key)}
              >
                Manage →
              </button>
            </div>
          );
        })}

        <div className="impl-card impl-card-index">
          <div className="impl-card-top">
            <span className="impl-card-ic" aria-hidden>
              🧭
            </span>
          </div>
          <h3>Template index</h3>
          <p>One register rolling up every template: type, linked controls, owner, status.</p>
          <button className="btn btn-grad impl-card-btn" onClick={() => setActive('index')}>
            Open index →
          </button>
        </div>
      </div>
    </section>
  );
}

/* ---------- "manage on the platform" banner ----------
 * One reference on the hub offers to graduate this whole phase from the static
 * kit to the live we/dpo platform, where the same items get owners, automation,
 * and an audit trail. */
function ManageBanner() {
  return (
    <div className="impl-manage">
      <div className="impl-manage-txt">
        <span className="impl-manage-eyebrow">we/dpo platform</span>
        <strong>Run your implementation on the we/dpo platform</strong>
        <span className="impl-manage-sub">
          Take these policies, procedures, evidence, and controls live: assign owners,
          automate evidence collection across the window, and keep everything versioned and
          audit-ready.
        </span>
      </div>
      <button
        className="btn btn-grad impl-manage-btn"
        onClick={() => window.open('https://app.wedpo.com', '_blank', 'noopener')}
      >
        Manage on we/dpo →
      </button>
    </div>
  );
}

/* ---------- one category panel ---------- */

function ActionButton({ item }: { item: ImplItem }) {
  if (item.download) {
    return (
      <a className="btn btn-ghost impl-tpl" href={item.download} download>
        Download
      </a>
    );
  }
  if (item.open) {
    return (
      <button
        className="btn btn-ghost impl-tpl"
        onClick={() => window.open(item.open, '_blank', 'noopener')}
      >
        {item.openLabel ?? 'Open template'}
      </button>
    );
  }
  return null;
}

function ItemPanel({
  title,
  sub,
  items,
  statusOf,
  onCycle,
  headerAction,
  evidenceMode = false,
  evidence = {},
  onAddEvidence,
  onRemoveEvidence,
}: {
  title: string;
  sub: string;
  items: ImplItem[];
  statusOf: (i: ImplItem) => Status;
  onCycle: (id: string, current: Status) => void;
  headerAction?: ReactNode;
  evidenceMode?: boolean;
  evidence?: Record<string, EvidenceFile[]>;
  onAddEvidence?: (id: string, files: EvidenceFile[]) => void;
  onRemoveEvidence?: (id: string, idx: number) => void;
}) {
  const done = items.filter((i) => statusOf(i) === 'done').length;
  return (
    <>
      <div className="impl-head">
        <h1 className="title" style={{ marginBottom: '0.2rem' }}>
          {title}
        </h1>
        <span className="impl-head-count">
          {done}/{items.length} done
        </span>
        {headerAction && <span className="impl-head-action">{headerAction}</span>}
      </div>
      <p className="sub">{sub}</p>
      <div className="impl-list">
        {items.map((i) => (
          <ImplRow
            key={i.id}
            item={i}
            status={statusOf(i)}
            onCycle={onCycle}
            evidenceMode={evidenceMode}
            files={evidence[i.id] ?? []}
            onAddEvidence={onAddEvidence}
            onRemoveEvidence={onRemoveEvidence}
          />
        ))}
      </div>
    </>
  );
}

function ImplRow({
  item,
  status,
  onCycle,
  evidenceMode,
  files,
  onAddEvidence,
  onRemoveEvidence,
}: {
  item: ImplItem;
  status: Status;
  onCycle: (id: string, current: Status) => void;
  evidenceMode: boolean;
  files: EvidenceFile[];
  onAddEvidence?: (id: string, files: EvidenceFile[]) => void;
  onRemoveEvidence?: (id: string, idx: number) => void;
}) {
  const meta = ST_META[status];
  const expandable = evidenceMode && (!!item.explain || !!item.example);
  const [open, setOpen] = useState(false);

  const onPick = async (list: FileList | null) => {
    if (!list || !list.length || !onAddEvidence) return;
    const read = await Promise.all(Array.from(list).map(readFile));
    onAddEvidence(item.id, read);
  };

  return (
    <div className={`impl-row${expandable ? ' impl-row-exp' : ''}`}>
      <div className="impl-row-top">
        <div className="impl-row-main">
          <div className="cname">{item.name}</div>
          {item.desc && <div className="impl-row-desc">{item.desc}</div>}
          <div className="impl-row-meta">
            <OwnerBadge role={item.role} />
            {item.controls.length > 0 && <ControlChips codes={item.controls} />}
            {item.cadence && <span className="recur">{item.cadence}</span>}
            {evidenceMode && files.length > 0 && (
              <span className="impl-ev-count">
                {files.length} file{files.length > 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>
        <div className="impl-row-actions">
          {expandable && (
            <div className="impl-icons">
              {files.length > 0 && (
                <button
                  className="impl-ic impl-ic-on"
                  onClick={() => setOpen((v) => !v)}
                  title={`${files.length} evidence file${files.length > 1 ? 's' : ''} uploaded`}
                  aria-label="Evidence uploaded"
                >
                  {ICON.clip}
                  <span className="impl-ic-badge">{files.length}</span>
                </button>
              )}
              <button
                className={`impl-ic${open ? ' open' : ''}`}
                onClick={() => setOpen((v) => !v)}
                title="Review"
                aria-label="Review"
                aria-expanded={open}
              >
                {ICON.eye}
              </button>
              <button
                className="impl-ic"
                onClick={() => setOpen(true)}
                title="Add or edit evidence"
                aria-label="Edit"
              >
                {ICON.pencil}
              </button>
            </div>
          )}
          <ActionButton item={item} />
          <button
            className={`status ${meta.cls} impl-status`}
            onClick={() => onCycle(item.id, status)}
            title="Click to update status"
          >
            {meta.label}
          </button>
        </div>
      </div>

      {expandable && open && (
        <div className="impl-detail">
          {item.explain && (
            <div className="impl-detail-block">
              <div className="impl-detail-h">What this is</div>
              <p>{item.explain}</p>
            </div>
          )}
          {item.example && (
            <div className="impl-detail-block">
              <div className="impl-detail-h">Example evidence</div>
              <p>{item.example}</p>
            </div>
          )}
          <div className="impl-detail-block">
            <div className="impl-detail-h">Evidence</div>
            {files.length > 0 ? (
              <ul className="impl-ev-list">
                {files.map((f, idx) => (
                  <li key={`${f.name}-${idx}`} className="impl-ev-item">
                    <span className="impl-ev-ic" aria-hidden>
                      {f.type.startsWith('image/') ? '🖼' : '📎'}
                    </span>
                    {f.dataUrl ? (
                      <a
                        className="impl-ev-name"
                        href={f.dataUrl}
                        target="_blank"
                        rel="noopener"
                      >
                        {f.name}
                      </a>
                    ) : (
                      <span className="impl-ev-name">{f.name}</span>
                    )}
                    <span className="impl-ev-meta">
                      {fmtBytes(f.size)} · {f.savedOn}
                    </span>
                    {onRemoveEvidence && (
                      <button
                        className="impl-ev-rm"
                        onClick={() => onRemoveEvidence(item.id, idx)}
                        title="Remove"
                      >
                        ×
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="impl-ev-empty">No evidence uploaded yet.</p>
            )}
            <label className="impl-ev-upload">
              <input
                type="file"
                multiple
                onChange={(e) => {
                  void onPick(e.target.files);
                  e.target.value = '';
                }}
              />
              <span>Upload evidence</span>
            </label>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- master index ---------- */

const INDEX_SECTIONS: { type: string; items: ImplItem[] }[] = [
  { type: 'Policy / Plan', items: POLICIES },
  { type: 'Procedure', items: PROCEDURES },
  { type: 'Evidence', items: EVIDENCE },
  { type: 'Technical', items: TECHNICAL },
  { type: 'Artifact', items: ARTIFACTS },
];

function IndexPanel({ statusOf }: { statusOf: (i: ImplItem) => Status }) {
  const rows = INDEX_SECTIONS.flatMap((s) =>
    s.items.map((i) => ({ ...i, type: s.type })),
  );
  const done = rows.filter((r) => statusOf(r) === 'done').length;
  return (
    <>
      <div className="impl-head">
        <h1 className="title" style={{ marginBottom: '0.2rem' }}>
          Template index
        </h1>
        <span className="impl-head-count">
          {done}/{rows.length} done
        </span>
        <span className="impl-head-action">
          <a className="btn btn-ghost" href={TEMPLATES_INDEX} download>
            Download full index
          </a>
        </span>
      </div>
      <p className="sub">
        The single register of every template this phase produces, ready to hand to your
        auditor. Status follows the work in each area above.
      </p>
      <div className="tblwrap">
        <table>
          <thead>
            <tr>
              <th style={{ width: '36%' }}>Item</th>
              <th>Type</th>
              <th>Linked controls</th>
              <th>Owner</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const meta = ST_META[statusOf(r)];
              return (
                <tr key={r.id}>
                  <td>
                    <div className="cname">{r.name}</div>
                  </td>
                  <td>
                    <span className="fchip">{r.type}</span>
                  </td>
                  <td>
                    {r.controls.length ? (
                      r.controls.map((c) => (
                        <span key={c} className="fchip">
                          {c}
                        </span>
                      ))
                    ) : (
                      <span className="impl-dash">-</span>
                    )}
                  </td>
                  <td>{roleShortLabels[r.role]}</td>
                  <td>
                    <span className={`status ${meta.cls}`}>{meta.label}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}
